import { useState, useEffect } from 'react';
import api from '../../../../api';
import Select from 'react-select';
import CountUp from 'react-countup';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { FaUsers, FaChartLine, FaDatabase, FaTrash } from 'react-icons/fa';
import { format } from "date-fns";
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

function PktScores() {
  const { accessToken, scope, client } = useAuth();

  const [data, setData] = useState({
    employees: 0,
    scopes: 0,
  });

  const [clients, setClients] = useState([]);
  const [pktScore, setPktScore] = useState(0);
  const [scopes, setScopes] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedScope, setSelectedScope] = useState(null);

  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pktData, setPktData] = useState({});

  // Date range state
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Control visibility of scope and date range inputs
  const [showScopeInput, setShowScopeInput] = useState(false);
  const [showDateRange, setShowDateRange] = useState(false);
  const [tablePercentage, setTablePercentage] = useState([]);

  // Fetch Clients from API
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        let baseUrls = getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrls}/client/getAllClient`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (response.data && Array.isArray(response.data)) {
          const clientOptions = response.data
            .filter((scopeItem) => client.includes(scopeItem._id))
            .map((client) => ({
              label: client.client,
              value: client._id,
            }));
          setClients(clientOptions);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [accessToken]);

  // Fetch Scopes based on selected Client
  const fetchScopes = async (clientId) => {
    setLoadingScopes(true);
    setScopes([]);
    try {
      let baseUrls = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrls}/qaDashboard/getClientByScope?clientId=${clientId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.data && Array.isArray(response.data.data)) {
        const scopeOptions = response.data.data.map((scopeItem) => ({
          label: scopeItem.scope,
          value: scopeItem._id,
        }));
        setScopes(scopeOptions);
      }
    } catch (error) {
      console.error('Error fetching scopes:', error);
      setScopes([]);
    } finally {
      setLoadingScopes(false);
    }
  };

  // Fetch Dashboard Stats
  useEffect(() => {
    const fetchAuditData = async () => {
      if (!accessToken) {
        console.log('Access token missing');
        return;
      }

      console.log('Fetching audit data...');
      let url;
      if (selectedClient && selectedScope && fromDate && toDate) {
        let baseUrls = getRoundRobinBaseUrl();
        url = `${baseUrls}/qaDashboard/getPKT?clientId=${selectedClient.value}&scopeId=${selectedScope.value}&startDate=${fromDate}&endDate=${toDate}`;
      } else if (selectedClient && selectedScope) {
        let baseUrls = getRoundRobinBaseUrl();
        url = `${baseUrls}/qaDashboard/getPKT?clientId=${selectedClient.value}&scopeId=${selectedScope.value}`;
      } else if (selectedClient) {
        let baseUrls = getRoundRobinBaseUrl();
        url = `${baseUrls}/qaDashboard/getPKT?clientId=${selectedClient.value}`;
      } else {
        let baseUrls = getRoundRobinBaseUrl();
        url = `${baseUrls}/qaDashboard/getPKT`;
      }

      try {
        const response = await api.get(url, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        console.log('API Response:', response);

        if (response.data.success) {
          const data = response.data.data[0];
          const tablePercentage = response.data.tablePercentage;
          console.log('data', data);
          setPktData({
            totalPktPoints: data.total_points || 0,
            totalPktEntries: data.total_entries || 0,
            pktPercentage: data.pkt_percentage || 0,
            totalEmployees: data.total_employee || 0, // Ensure this field exists in API response
          });
          setTablePercentage(tablePercentage);
        } else {
          console.error('API returned an error:', response.data.message);
        }
      } catch (error) {
        console.error('Error fetching audit data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditData();
  }, [accessToken, selectedClient, selectedScope, fromDate, toDate]);

  const handleSelectedClient = (selected) => {
    console.log(selected);
    setSelectedClient(selected);
    setSelectedScope(null);
    setFromDate(''); // Reset From Date
    setToDate(''); // Reset To Date
    setScopes([]); // Clear scopes before fetching new ones
    fetchScopes(selected.value); // Fetch scopes when a new client is selected
    setShowScopeInput(true); // Show the scope input field when a client is selected
    setShowDateRange(false); // Hide date range input until scope is selected
  };

  // Handle Clear Button
  const handleClear = () => {
    setClients([]);
    setSelectedClient(null);
    setSelectedScope(null);
    setFromDate('');
    setToDate('');
    setScopes([]);
    setPktData({});
    setShowScopeInput(false);
    setShowDateRange(false);
  };

  return (
    <div className="p-8 bg-gray-100 min-h-screen">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent animate-spin rounded-full"></div>
        </div>
      )}

      {/* <div className="text-center my-8">
        <h2 className="text-4xl font-extrabold text-gray-900">PKT SCORE</h2>
        <p className="text-gray-600 text-lg">Performance Key Tracker Summary</p>
      </div> */}

      <div className="grid grid-cols-4 md:grid-cols-4 gap-6 mb-8">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Select Client</label>
          <Select
            options={clients}
            isLoading={loadingClients}
            placeholder="Select Client..."
            className="w-full"
            onChange={(selected) => {
              handleSelectedClient(selected);
            }}
          />
        </div>

        {showScopeInput && (
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Select Scope</label>
            <Select
              options={scopes}
              isLoading={loadingScopes}
              placeholder="Select Scope..."
              className="w-full"
              onChange={(selected) => {
                setSelectedScope(selected);
                setShowDateRange(true);
              }}
              isDisabled={!selectedClient}
            />
          </div>
        )}

        {showDateRange && (
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-[#EE1D23] text-[#1A2B5C] rounded-md"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-3 py-2 border border-[#EE1D23] text-[#1A2B5C] rounded-md"
              />
            </div>
            <div>
              <button
                className="bg-red-500 hover:bg-red-700 text-white font-bold mt-8 py-2 px-4 rounded transition-all"
                onClick={handleClear}
              >
                <FaTrash className="inline-block mr-2" />
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="flex items-center justify-between p-5 bg-gradient-to-r from-red-500 to-blue-700 text-white rounded-lg shadow-md cursor-pointer transition-all duration-300 hover:shadow-xl">
          <div className="flex items-center space-x-4">
            <FaUsers className="text-3xl" />
            <h3 className="text-lg font-semibold">Total Entries</h3>
          </div>
        </div>

        <div className="flex items-center justify-between p-5 bg-gradient-to-r from-red-500 to-blue-700 text-white rounded-lg shadow-md cursor-pointer transition-all duration-300 hover:shadow-xl">
          <div className="flex items-center space-x-4">
            <FaChartLine className="text-3xl" />
            <h3 className="text-lg font-semibold">PKT Score</h3>
          </div>
        </div>

        <div className="flex items-center justify-between p-5 bg-gradient-to-r from-red-500 to-blue-700 text-white rounded-lg shadow-md cursor-pointer transition-all duration-300 hover:shadow-xl">
          <div className="flex items-center space-x-4">
            <FaUsers className="text-3xl" />
            <h3 className="text-lg font-semibold">Total Employees</h3>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-500">
        {/* Total Entries */}
        <div className="p-6 bg-white rounded-xl shadow-lg text-center transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
          <h6 className="text-lg font-semibold text-gray-700">Total Entries</h6>
          <FaDatabase className="text-5xl text-blue-700 mx-auto mb-2" />
          <p className="text-4xl font-bold text-gray-900 mt-2">
            <CountUp start={0} end={pktData?.totalPktEntries || 0} duration={2} separator="," />
          </p>
        </div>

        {/* PKT Score */}
        <div className="p-6 bg-white rounded-xl shadow-lg text-center transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
          <h6 className="text-lg font-semibold text-gray-700">PKT Score</h6>
          <FaChartLine className="text-5xl text-green-700 mx-auto mb-2" />
          <p className="text-4xl font-bold text-gray-900 mt-2">
            <CountUp start={0} end={pktData?.pktPercentage || 0} duration={2} separator="," />
          </p>
        </div>

        {/* Total Employees */}
        <div className="p-6 bg-white rounded-xl shadow-lg text-center transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
          <h6 className="text-lg font-semibold text-gray-700">Total Employees</h6>
          <FaUsers className="text-5xl text-red-700 mx-auto mb-2" />
          <p className="text-4xl font-bold text-gray-900 mt-2">
            <CountUp start={0} end={pktData?.totalEmployees || 0} duration={2} separator="," />
          </p>
        </div>
      </div>

      {/* PKT Data Table */}
      <div className="p-6 bg-white rounded-xl shadow-lg mt-8 transition-all duration-500">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-gray-900">📊 PKT Data Table</h3>
          <p className="text-gray-700 font-semibold">
            Total Records:{' '}
            <span className="text-blue-600 text-xl font-bold">
              <CountUp start={0} end={tablePercentage?.length || 0} duration={1.5} separator="," />
            </span>
          </p>
        </div>

        {/* Scrollable Table Container */}
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto rounded-lg shadow border border-gray-300">
          <table className="min-w-full bg-gray-50 border border-gray-300 rounded-lg">
            <thead className="bg-gradient-to-r from-blue-600 to-red-500 text-white sticky top-0 z-10">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wide">Employee ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wide">Employee Name</th>
                <th className="px-6 py-4 text-center text-sm font-semibold uppercase tracking-wide">Total Points</th>
                <th className="px-6 py-4 text-center text-sm font-semibold uppercase tracking-wide">Worked Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tablePercentage.length > 0 ? (
                tablePercentage.map((entry, index) => (
                  <tr
                    key={index}
                    className={`transition-all duration-300 hover:bg-blue-100 ${index % 2 === 0 ? 'bg-gray-100' : 'bg-white'}`}
                  >
                    <td className="px-6 py-4 text-gray-900 font-medium">{entry.EmployeeId}</td>
                    <td className="px-6 py-4 text-gray-900">{entry.employeeName || 'N/A'}</td>
                    <td className="px-6 py-4 text-center text-gray-900 font-bold text-lg">{entry.TotalPoints}</td>
                    <td className="px-6 py-4 text-center text-gray-900">{format(new Date(entry.WorkedDate), 'MM/dd/yyyy')}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <span className="text-4xl">📉</span>
                      <p className="text-lg mt-2">No data available</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PktScores;
